import { useEffect, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import CircularProgress from '@mui/material/CircularProgress';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Button, Input, PhoneInput } from '@/shared/components/ui';
import { ProtectedRoute } from '@/shared/components/ProtectedRoute';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authApi } from '@/features/auth/api/auth.api';
import { userApi } from '@/features/users/api/user.api';
import { TravelerAvatarUpload } from '@/features/users/components/TravelerAvatarUpload';
import {
  fullNameFromParts,
  resolveProfileNames,
} from '@/features/users/utils/profileNames';
import { GUIDE_ENTRY_ROUTE } from '@/features/auth/constants';
import { GUIDE_ROUTES } from '@/features/guide/constants/routes';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import {
  isGoogleAuthWithEmail,
  isValidEmail,
  resolveCheckoutEmail,
} from '@/features/booking/utils/paymentEmail';
import {
  fromE164India,
  indianMobileSchemaMessage,
  toE164India,
} from '@/shared/utils/phone';
import {
  dashboardLightHeroSx,
  dashboardSectionPanelSx,
} from '@/shared/styles/surfaces';

const profileSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(50),
  lastName: z.string().trim().min(1, 'Last name is required').max(50),
  contactPhone: z.string().trim().regex(/^\d{10}$/, indianMobileSchemaMessage),
  email: z.string().trim().email('Enter a valid email address').or(z.literal('')),
  emergencyContactName: z.string().trim().max(120),
  emergencyContactNumber: z
    .string()
    .trim()
    .refine((v) => v === '' || /^\d{10}$/.test(v), indianMobileSchemaMessage),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

function formValuesFromUser(user: NonNullable<ReturnType<typeof useAuth>['user']>): ProfileFormValues {
  const { firstName, lastName } = resolveProfileNames(user);
  return {
    firstName,
    lastName,
    contactPhone: fromE164India(user.contactPhone ?? user.phone),
    email: resolveCheckoutEmail(user),
    emergencyContactName: user.emergencyContactName ?? '',
    emergencyContactNumber: fromE164India(user.emergencyContactNumber),
  };
}

function ProfileQuickLink({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Paper
      component={RouterLink}
      to={to}
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        textDecoration: 'none',
        color: 'inherit',
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          borderColor: 'primary.200',
          boxShadow: 'var(--shadow-sm)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: 'rgba(37, 99, 235, 0.08)',
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
          {description}
        </Typography>
      </Box>
    </Paper>
  );
}

function ProfileContent() {
  const { user, updateUser, setMe, accountLabel, isApprovedGuide, isAdmin } = useAuth();
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(true);
  const googleEmailLocked = isGoogleAuthWithEmail(user);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      contactPhone: '',
      email: '',
    },
  });

  const watchedFirstName = watch('firstName');
  const watchedLastName = watch('lastName');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const me = await authApi.getMe();
        if (cancelled) return;
        setMe(me);
        reset(formValuesFromUser(me.user));
        setLoadError(null);
      } catch {
        if (!cancelled) setLoadError('Could not refresh your profile. Showing saved details.');
      } finally {
        if (!cancelled) setIsRefreshing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reset, setMe]);

  const saveMutation = useMutation({
    mutationFn: async (values: ProfileFormValues) => {
      const email = values.email.trim();
      if (!googleEmailLocked && email && !isValidEmail(email)) {
        throw new Error('Enter a valid email address');
      }

      const displayName = fullNameFromParts(values.firstName, values.lastName);

      const updatedProfile = await userApi.updateProfile({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      });

      const updatedContact = await userApi.updateContact({
        displayName,
        contactPhone: toE164India(values.contactPhone),
        email: googleEmailLocked ? undefined : email || undefined,
        emergencyContactName: values.emergencyContactName.trim() || null,
        emergencyContactNumber: values.emergencyContactNumber.trim()
          ? toE164India(values.emergencyContactNumber)
          : null,
      });

      return { ...updatedProfile, ...updatedContact };
    },
    onSuccess: (updatedUser) => {
      updateUser(updatedUser);
      reset(formValuesFromUser(updatedUser));
    },
  });

  if (!user) return null;

  const memberSince = user.createdAt
    ? dayjs(user.createdAt).format('D MMM YYYY')
    : null;

  const heroName =
    fullNameFromParts(watchedFirstName, watchedLastName) ||
    fullNameFromParts(user.firstName ?? '', user.lastName ?? '') ||
    user.displayName?.trim() ||
    'Your profile';

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        backgroundImage:
          'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(37, 99, 235, 0.05), transparent)',
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Paper
          elevation={0}
          sx={{
            ...dashboardLightHeroSx,
            p: { xs: 2.5, sm: 3, md: 3.5 },
            mb: 3,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2.5,
              flexWrap: 'wrap',
            }}
          >
            <TravelerAvatarUpload
              user={user}
              onUpdated={updateUser}
              size={84}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  letterSpacing: '-0.03em',
                  fontSize: { xs: '1.5rem', md: '1.85rem' },
                  lineHeight: 1.2,
                }}
              >
                {heroName}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                Manage your account details and booking contact information.
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                <Chip label={accountLabel} size="small" color="primary" variant="outlined" />
                {user.phoneVerified && (
                  <Chip label="Phone verified" size="small" variant="outlined" />
                )}
                {user.emailVerified && (
                  <Chip label="Email verified" size="small" variant="outlined" />
                )}
                {memberSince && (
                  <Chip label={`Member since ${memberSince}`} size="small" variant="outlined" />
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper elevation={0} sx={{ ...dashboardSectionPanelSx }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                Account details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Update how your name and contact details appear on bookings.
              </Typography>

              {loadError && (
                <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
                  {loadError}
                </Alert>
              )}

              {saveMutation.isSuccess && !isDirty && (
                <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>
                  Profile updated successfully.
                </Alert>
              )}

              {saveMutation.isError && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                  {(saveMutation.error as Error).message || 'Failed to save profile. Please try again.'}
                </Alert>
              )}

              {isRefreshing ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress color="primary" />
                </Box>
              ) : (
                <Box
                  component="form"
                  onSubmit={handleSubmit((values) => saveMutation.mutate(values))}
                  noValidate
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                    Personal information
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Input
                        {...register('firstName')}
                        label="First name"
                        fullWidth
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message || 'Shown to guides on your bookings'}
                        autoComplete="given-name"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Input
                        {...register('lastName')}
                        label="Last name"
                        fullWidth
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message || 'Shown to guides on your bookings'}
                        autoComplete="family-name"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                    Contact details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="contactPhone"
                        control={control}
                        render={({ field }) => (
                          <PhoneInput
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            label="Contact phone"
                            fullWidth
                            error={!!errors.contactPhone}
                            helperText={
                              errors.contactPhone?.message || 'Enter your 10-digit mobile number'
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Input
                        {...register('email')}
                        label="Email"
                        type="email"
                        fullWidth
                        error={!!errors.email}
                        helperText={
                          errors.email?.message ||
                          (googleEmailLocked
                            ? 'Linked to your Google account'
                            : 'Used for payment receipts and updates')
                        }
                        InputProps={{ readOnly: googleEmailLocked }}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Emergency contact
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Used by SOS on active trips to quickly notify someone you trust. Trplin is not
                    an emergency response service.
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Input
                        {...register('emergencyContactName')}
                        label="Contact name"
                        fullWidth
                        error={!!errors.emergencyContactName}
                        helperText={
                          errors.emergencyContactName?.message || 'e.g. Mom, spouse, or friend'
                        }
                        autoComplete="name"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="emergencyContactNumber"
                        control={control}
                        render={({ field }) => (
                          <PhoneInput
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            label="Contact phone"
                            fullWidth
                            error={!!errors.emergencyContactNumber}
                            helperText={
                              errors.emergencyContactNumber?.message ||
                              'Optional — leave blank to clear'
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                      type="submit"
                      intent="primary-dark"
                      disabled={saveMutation.isPending || !isDirty}
                    >
                      {saveMutation.isPending ? 'Saving…' : 'Save changes'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ ...dashboardSectionPanelSx, mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Account info
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {user.phone && (
                  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                    <PhoneOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Login phone
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {user.phone}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {user.email && (
                  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                    <EmailOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Account email
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  <PersonOutlineIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.25 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Account type
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {accountLabel}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ ...dashboardSectionPanelSx }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Quick links
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <ProfileQuickLink
                  to="/my-bookings"
                  icon={<EventNoteOutlinedIcon fontSize="small" />}
                  title="My bookings"
                  description="View upcoming trips and past travel history."
                />
                <ProfileQuickLink
                  to="/guides"
                  icon={<ExploreOutlinedIcon fontSize="small" />}
                  title="Explore guides"
                  description="Find local guides for your next destination."
                />
                {isApprovedGuide ? (
                  <ProfileQuickLink
                    to={GUIDE_ROUTES.HOME}
                    icon={<DashboardOutlinedIcon fontSize="small" />}
                    title="Guide dashboard"
                    description="Manage bookings, pricing, and availability."
                  />
                ) : (
                  !isAdmin && (
                    <ProfileQuickLink
                      to={GUIDE_ENTRY_ROUTE}
                      icon={<DashboardOutlinedIcon fontSize="small" />}
                      title="Become a guide"
                      description="Share your city and start hosting travelers."
                    />
                  )
                )}
                {isAdmin && (
                  <ProfileQuickLink
                    to={ADMIN_ROUTES.DASHBOARD}
                    icon={<DashboardOutlinedIcon fontSize="small" />}
                    title="Admin console"
                    description="Manage guides, travelers, and platform operations."
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export function ProfilePage() {
  return (
    <ProtectedRoute>
      <ProfileContent />
    </ProtectedRoute>
  );
}
